/**
 * @fileoverview Largest-remainder apportionment helpers for the banking feature.
 * @description Ported from the InsightBox Banking design system's own `data/derived.ts`. Used
 * everywhere a mix/breakdown is split from a whole (deposit mix, loan mix, NPA sector shares,
 * share-capital bands, ...) so the displayed parts always sum to exactly the displayed total —
 * independent per-part rounding (a bare `.toFixed(2)` on each) can leave the parts a cent or a
 * unit off from the total once a reader actually adds up what's printed on the page, even though
 * the underlying unrounded numbers matched exactly.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

/**
 * @description Splits `raw` (fractional shares that sum to `total`) into integers that sum to
 * EXACTLY `total` — floors each share, then hands the leftover units to the entries with the
 * largest fractional remainders. Standard "largest remainder" apportionment; avoids the classic
 * bug where several independently-rounded shares silently drift from the total they're meant to
 * add up to.
 * @param {number[]} raw - The fractional shares, summing to (approximately) `total`.
 * @param {number} total - The exact integer total the rounded shares must sum to.
 * @returns {number[]} Integers, in the same order as `raw`, summing to exactly `total`.
 */
export function largestRemainderRound(raw: number[], total: number): number[] {
  const floors = raw.map(Math.floor)
  const remaining = total - floors.reduce((sum, value) => sum + value, 0)
  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac)
  const result = [...floors]
  for (let k = 0; k < remaining; k++) {
    const slot = order[k]
    if (slot) {
      result[slot.index] = (result[slot.index] ?? 0) + 1
    }
  }
  return result
}

/**
 * @description Same idea as `largestRemainderRound`, but for a displayed money/decimal total
 * instead of a whole-number count — rounds `raw` to `decimals` places so the ON-SCREEN parts
 * always sum to the ON-SCREEN total.
 * @param {number[]} raw - The fractional shares, summing to (approximately) `total`.
 * @param {number} total - The exact decimal total the rounded shares must sum to.
 * @param {number} [decimals] - Decimal places to preserve. Defaults to 2.
 * @returns {number[]} Values rounded to `decimals` places, summing to exactly `total`.
 */
export function largestRemainderRoundDecimal(raw: number[], total: number, decimals = 2): number[] {
  const scale = 10 ** decimals
  const targetUnits = Math.round(total * scale)
  const rounded = largestRemainderRound(
    raw.map((value) => value * scale),
    targetUnits,
  )
  return rounded.map((value) => value / scale)
}
